import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, Download, Home } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { SHOP } from "@/constants/testIds";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!sessionId) {
        if (!cancelled) setStatus("failed");
        return;
      }
      let attempts = 0;

      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (cancelled) return;
          if (data.payment_status === "paid" && data.order) {
            setOrder(data.order);
            setStatus("paid");
            clear();
            sessionStorage.removeItem("urbx_pending_session");
            track("order_paid");
            return;
          }
          if (data.status === "expired") {
            setStatus("failed");
            return;
          }
          attempts += 1;
          if (attempts >= MAX_POLLS) {
            setStatus("timeout");
            return;
          }
          setTimeout(poll, POLL_INTERVAL);
        } catch (e) {
          attempts += 1;
          if (cancelled) return;
          if (attempts >= MAX_POLLS) setStatus("failed");
          else setTimeout(poll, POLL_INTERVAL);
        }
      };
      await poll();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  const qrUrl = order ? `${API}/qr/${order.booking_ref}.png` : null;

  return (
    <div className="min-h-screen bg-[#FDF0DF] flex flex-col">
      <Header />
      <main className="flex-1 max-w-[1100px] mx-auto px-5 md:px-10 py-12 md:py-20 w-full">
        {status === "checking" && (
          <div data-testid={SHOP.successStatus} className="text-center py-16">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#14230A]" />
            <div className="font-cond uppercase tracking-[0.3em] text-xs mt-6 opacity-60">
              Vérification du paiement
            </div>
            <h1 className="font-display text-4xl md:text-5xl mt-2">On valide ton drop…</h1>
            <p className="mt-3 text-[#14230A]/70">Ça prend quelques secondes. Ne ferme pas la page.</p>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid={SHOP.successStatus} className="text-center py-16">
            <AlertTriangle className="w-12 h-12 mx-auto text-[#FF4D00]" />
            <h1 className="font-display text-4xl md:text-5xl mt-4">Paiement en attente</h1>
            <p className="mt-3 text-[#14230A]/70 max-w-md mx-auto">
              On n’a pas eu la confirmation tout de suite. Si tu as bien payé, le ticket arrive par
              email d’ici quelques minutes.
            </p>
            <Link
              to="/"
              data-testid={SHOP.successHome}
              className="mt-8 inline-flex items-center gap-2 px-6 h-12 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-sm"
            >
              <Home className="w-4 h-4" /> Retour à l’accueil
            </Link>
          </div>
        )}

        {status === "failed" && (
          <div data-testid={SHOP.successStatus} className="text-center py-16">
            <AlertTriangle className="w-12 h-12 mx-auto text-[#C53A1B]" />
            <h1 className="font-display text-4xl md:text-5xl mt-4">Paiement non finalisé</h1>
            <p className="mt-3 text-[#14230A]/70 max-w-md mx-auto">
              Le paiement n’a pas pu aboutir. Pas de panique, ton panier est toujours disponible.
            </p>
            <Link
              to="/"
              data-testid={SHOP.successHome}
              className="mt-8 inline-flex items-center gap-2 px-6 h-12 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-sm"
            >
              <Home className="w-4 h-4" /> Réessayer
            </Link>
          </div>
        )}

        {status === "paid" && order && (
          <div data-testid={SHOP.successStatus} className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 h-8 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.25em] text-[11px]">
                <CheckCircle2 className="w-4 h-4 text-[#FF4D00]" /> Paiement confirmé
              </div>
              <h1 className="font-display text-5xl md:text-7xl leading-[0.9] mt-4">
                Ton ticket <br />
                <span className="text-[#FF4D00]">est prêt</span>.
              </h1>
              <p className="mt-5 text-[#14230A]/75 max-w-md">
                On a envoyé une copie sur <b>{order.customer_email}</b>. Présente le QR au stand
                Trésors de Guyane le 20 juin.
              </p>

              <div className="mt-8 border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-5">
                <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                  Récap commande
                </div>
                <ul className="mt-3 space-y-2 text-sm">
                  {order.items.map((it) => (
                    <li key={it.product_id} className="flex justify-between">
                      <span className="opacity-90">
                        {it.name} <span className="opacity-60">× {it.quantity}</span>
                      </span>
                      <span className="font-cond">{it.line_total_eur.toFixed(2)} €</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-[#FDF0DF]/20">
                  <span className="font-cond uppercase tracking-[0.2em] text-sm">Total payé</span>
                  <span className="font-display text-3xl text-[#FF4D00]">
                    {order.total_eur.toFixed(2)} €
                  </span>
                </div>
              </div>

              <div className="mt-8 flex gap-3 flex-wrap">
                <a
                  href={qrUrl}
                  download={`urbx-ticket-${order.booking_ref}.png`}
                  data-testid={SHOP.successDownload}
                  className="inline-flex items-center gap-2 px-5 h-12 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#FF4D00]"
                >
                  <Download className="w-4 h-4" /> Télécharger le QR
                </a>
                <Link
                  to="/"
                  data-testid={SHOP.successHome}
                  className="inline-flex items-center gap-2 px-5 h-12 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#14230A] hover:text-[#FDF0DF]"
                >
                  <Home className="w-4 h-4" /> Accueil
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="border-2 border-[#14230A] p-5 md:p-6 bg-[#FDF0DF]">
                <div className="bg-[#14230A] p-4 flex items-center justify-center">
                  <img
                    data-testid={SHOP.successQr}
                    src={qrUrl}
                    alt="QR ticket"
                    className="w-full max-w-[320px] bg-[#FDF0DF] p-2"
                  />
                </div>
                <div className="mt-4 text-center">
                  <div className="font-cond uppercase tracking-[0.3em] text-[11px] opacity-60">
                    Référence
                  </div>
                  <div
                    data-testid={SHOP.successRef}
                    className="font-display text-3xl md:text-4xl mt-1 tracking-wider"
                  >
                    {order.booking_ref}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center text-xs text-[#14230A]/60 font-cond uppercase tracking-widest">
                Samedi 20 juin · Grand’Place Roubaix
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
